/**
 * CSV export helpers + date-range helper for the Reports page.
 *
 * Extracted from Reports.tsx during the Maintainability Sprint 2026-04-19
 * (Task 1.2). Behavior and French copy preserved byte-for-byte.
 *
 * `buildRevenueCsv` is exported through Reports.tsx as well so that existing
 * vitest specs continue to resolve `import { buildRevenueCsv } from "../Reports"`.
 */

import { toast } from "sonner";
import type {
  RevenuePeriod,
  RevenueResponse,
} from "@/lib/admin-api/finance";

// ---------------------------------------------------------------------------
// Shared date range helper — computes the query window for a given period
// ---------------------------------------------------------------------------

export function computeRevenueDateRange(period: RevenuePeriod): {
  readonly dateFrom: string;
  readonly dateTo: string;
} {
  const now = new Date();
  const to = now.toISOString().split("T")[0];
  const from = new Date(now);
  if (period === "day") from.setDate(from.getDate() - 30);
  else if (period === "week") from.setDate(from.getDate() - 84);
  else if (period === "month") from.setMonth(from.getMonth() - 12);
  else if (period === "quarter") from.setMonth(from.getMonth() - 24);
  else from.setFullYear(from.getFullYear() - 3);
  return { dateFrom: from.toISOString().split("T")[0], dateTo: to };
}

// ---------------------------------------------------------------------------
// Generic download helper
// ---------------------------------------------------------------------------

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Revenue CSV builder
// ---------------------------------------------------------------------------

/**
 * Build a UTF-8 CSV (with BOM so Excel recognises accents) from the
 * currently-loaded revenue series. Uses `;` as the field separator because
 * `fr-SN` number formatting uses `,` as the decimal separator.
 */
export function buildRevenueCsv(data: RevenueResponse): string {
  const bom = "\uFEFF";
  const header = "Date;Revenu (FCFA);Commandes;Panier moyen (FCFA)";
  const rows = data.series.map(
    (p) =>
      `${p.date};${p.revenue_fcfa};${p.orders};${p.avg_order_fcfa}`,
  );
  const totalsRow = `TOTAL;${data.kpis.total_fcfa};${data.kpis.orders_count};${data.kpis.avg_order_fcfa}`;
  return bom + [header, ...rows, totalsRow].join("\n");
}

export function exportRevenueCsv(
  data: RevenueResponse | undefined,
  period: RevenuePeriod,
): void {
  if (!data || data.series.length === 0) {
    toast.error("Aucune donnee a exporter pour cette periode.");
    return;
  }
  const csv = buildRevenueCsv(data);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const date = new Date().toISOString().split("T")[0];
  downloadBlob(blob, `rapport-revenu-${period}-${date}.csv`);
  toast.success("Export CSV telecharge.");
}
